import { Box, Popover, Typography, Skeleton, useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateActivityDuration,
  updateTime,
} from "../../store/slice/Activity.slice";
import { useTheme } from "@emotion/react";
import { formatTimeString, format_Number } from "../../utils/utils";

const TimeComponent = ({ isOpen, handleClose, Ref, isDuration = false }) => {
  const anchorEl = Ref;
  const { css } = useSelector((store) => store?.widgetSettings);
  const timeCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Time"
  )?.fields;

  const [anchorOriginVert, setAnchorOriginVert] = useState("bottom");
  const [transformOriginVert, setTransformOriginVert] = useState("top");

  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const {
    reservType,
    selectedDate,
    time,
    freeTablesData,
    activityDuration,
    languageWords,
  } = useSelector((store) => store?.Activity);

  useEffect(() => {
    if (!isOpen || !anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    if (rect.bottom + 400 > viewportHeight) {
      setAnchorOriginVert("top");
      setTransformOriginVert("bottom");
    } else {
      setAnchorOriginVert("bottom");
      setTransformOriginVert("top");
    }
  }, [isOpen, anchorEl]);

  const tileH = isMobile ? "40px" : "51px";

  return (
    <Popover
      open={isOpen && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      PaperProps={{
        sx: {
          width: isMobile ? "95vw" : isDuration ? "650px" : "480px",
          maxWidth: "95vw",
          maxHeight: "min(450px, 80vh)",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          zIndex: 3000,
        },
      }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      <Box
        sx={{
          backgroundColor: timeCss?.TimesBackgroundColor,
          p: { xs: 1.5, sm: 2 },
        }}
        className="times"
      >
        {/* ── Heading ── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mt: { xs: 0.5, sm: 1 },
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: theme?.time?.itemTextColor,
              fontSize: { xs: "12px", sm: "13px", md: "14px" },
              fontWeight: "bold",
            }}
          >
            {isDuration
              ? languageWords?.labelSelectDuration
              : languageWords?.labelSelectTime}
          </Typography>
        </Box>

        {/* ── Tiles Grid ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isDuration
              ? { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" } 
              : { xs: "repeat(3, 1fr)", sm: "repeat(3, 1fr)" }, 
            gap: { xs: "0.4rem", sm: "0.75rem", md: "1rem" },
            width: "100%",
          }}
        >
          {isLoading ? (
            Array.from(new Array(6)).map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: "100%",
                  height: tileH,
                  border: "1px solid #C9C9C9",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "5px",
                }}
              >
                <Skeleton variant="text" width={50} height={24} />
              </Box>
            ))
          ) : isDuration ? (
            // ── Duration tiles ──
            reservType?.products
              ?.filter((prod) => prod?.price > 0)
              ?.map((item, index) => {
                const isActive = activityDuration?.name === item?.name;
                return (
                  <Box
                    key={index}
                    onClick={() => {
                      dispatch(updateActivityDuration(item));
                      dispatch(updateTime(""));
                      handleClose();
                    }}
                    sx={{
                      width: "100%",     
                      minHeight: tileH,
                      border: "1px solid",
                      p: { xs: 0.75, sm: 1 },
                      borderColor: timeCss?.TimesTimeBorderColor,
                      backgroundColor: isActive
                        ? timeCss?.TimesTimeBackgroundColorActive
                        : timeCss?.TimesTimeBackgroundColor,
                      color: isActive
                        ? timeCss?.TimesTimeTextColorActive
                        : timeCss?.TimesTimeTextColor,
                      "&:hover": {
                        backgroundColor: timeCss?.TimesTimeBackgroundColorHover,
                        "& .text": {
                          color: isActive
                            ? timeCss?.TimesTimeTextColor
                            : timeCss?.TimesTimeTextColorHover,
                        },
                      },
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "background-color 0.3s ease",
                      borderRadius: "5px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "11px", sm: "13px", md: "14px" },
                        }}
                      >
                        {formatTimeString(item?.timeText, languageWords)}
                      </Typography>
                      <Typography
                        component="small"
                        sx={{
                          fontSize: { xs: "10px", sm: "12px" },
                          color: isActive ? "white" : "grey",
                        }}
                      >
                        &euro;{format_Number(item?.price)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
          ) : (
            // ── Time tiles ──
            freeTablesData?.map((item, index) => {
              const timeStr = typeof item === "string" ? item : item?.time ?? "";
              const isLocked = typeof item === "object" ? !!item?.locked : false;
              const isActive = time === item;

              return (
                <Box
                  key={index}
                  onClick={() => {
                    if (!isLocked) {
                      dispatch(updateTime(item));
                      handleClose();
                    }
                  }}
                  sx={{
                    width: "100%",      
                    height: tileH,
                    border: "1px solid",
                    borderColor: timeCss?.TimesTimeBorderColor,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: isLocked ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease",
                    borderRadius: "5px",
                    backgroundColor: isActive
                      ? timeCss?.TimesTimeBackgroundColorActive
                      : timeCss?.TimesTimeBackgroundColor,
                    color: isActive
                      ? timeCss?.TimesTimeTextColorActive
                      : timeCss?.TimesTimeTextColor,
                    "&:hover": {
                      backgroundColor: timeCss?.TimesTimeBackgroundColorHover,
                      "& .text": {
                        color: isLocked
                          ? timeCss?.TimesTimeTextColor
                          : timeCss?.TimesTimeTextColorHover,
                      },
                    },
                    textDecoration: isLocked ? "line-through" : "none",
                    opacity: isLocked ? 0.5 : 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", sm: "13px", md: "14px" },
                    }}
                  >
                    {timeStr.substring(0, 5)}
                  </Typography>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Popover>
  );
};

export default TimeComponent;